import React, { useState } from "react";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Button } from "./Button";
import { Input, InputProps } from "./Input";

/** Bullet / asterisk runs read as a filled password. Never use them as a hint. */
const MASK_GLYPHS = /^[\u2022*.\u00b7]+$/;

export type PasswordInputProps = Omit<InputProps, "type">;

/**
 * A password field that looks empty until the user types, and can be revealed
 * so they can check what they entered. Mask glyphs in `placeholder` are dropped.
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ placeholder, className, disabled, id, ...rest }, ref) => {
    const [shown, setShown] = useState(false);
    const hint =
      placeholder && MASK_GLYPHS.test(placeholder.trim()) ? undefined : placeholder;
    const label = shown ? "Hide password" : "Show password";

    return (
      <div className="relative">
        <Input
          {...rest}
          ref={ref}
          id={id}
          type={shown ? "text" : "password"}
          placeholder={hint}
          disabled={disabled}
          spellCheck={false}
          className={["pr-12 sm:pr-10 [@media(pointer:coarse)]:pr-12", className].filter(Boolean).join(" ")}
        />
        <Button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShown((open) => !open)}
          aria-pressed={shown}
          aria-controls={id}
          disabled={disabled}
          variant="ghost"
          size="icon"
          className="absolute right-0 top-1/2 min-h-11 min-w-11 -translate-y-1/2 hover:bg-transparent focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-od-surface sm:right-1 sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:right-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
          aria-label={label}
          title={label}
        >
          {shown ? (
            <VisibilityOffOutlinedIcon sx={{ fontSize: 17 }} />
          ) : (
            <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
          )}
        </Button>
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";
